/**
 * Date rendering. Every date in the application goes through here.
 *
 * Two rules, both non-negotiable:
 *  - The server stores and sends UTC. The viewer sees THEIR OWN timezone. Never hard-code a
 *    zone and never call toLocale* directly at a call site.
 *  - Output is stable and unambiguous: a numeric day and a short month name, so 03/04 is
 *    never read as the wrong date.
 *  - Time is 12-hour with AM/PM, and seconds appear only when the instant actually carries
 *    them. `2-digit` hours rather than numeric: these render in mono, tabular-nums columns,
 *    and a 1 vs 11 hour would shift every row beside it.
 */

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_PARTS = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
} as const;

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, DATE_TIME_PARTS);

const DATE_TIME_SECONDS_FORMAT = new Intl.DateTimeFormat(undefined, {
  ...DATE_TIME_PARTS,
  second: '2-digit',
});

/**
 * The API sends UTC. .NET serializes some DateTime values without a trailing Z, which
 * JavaScript would then interpret as local time and silently shift by the viewer's offset.
 * Appending the marker when it is missing is what keeps the two agreeing.
 */
function parseUtc(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const normalized = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Placeholder for an absent date. An em dash, never an empty cell. */
export const EMPTY_DATE = '—';

export function formatDate(value: string | null | undefined): string {
  const date = parseUtc(value);
  return date ? DATE_FORMAT.format(date) : EMPTY_DATE;
}

/**
 * A **calendar date** with no instant behind it — a `DateOnly` on the server: an effectivity
 * date, a date of birth, a period end.
 *
 * It is NOT converted to the viewer's zone, and that is the whole point. An instant is the
 * same moment everywhere and is rightly shown in local time; a calendar date is not a moment
 * at all. Run "2026-01-01" through the instant path and a reader west of UTC is told the rate
 * takes effect on 31 December — a different day from the one on the form they filled in.
 *
 * So the parts are read straight off the string and formatted as a local date with those
 * parts, which is the one case where not converting is the correct behaviour.
 */
export function formatDateOnly(value: string | null | undefined): string {
  const date = parseCalendarDate(value);

  if (!date) {
    // Not a calendar date after all — fall back rather than inventing one.
    return value ? formatDate(value) : EMPTY_DATE;
  }

  return DATE_FORMAT.format(date);
}

const CLOCK_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

/**
 * A **time of day** with no instant behind it — a `TimeOnly` on the server (`HH:mm` or
 * `HH:mm:ss`): when a press conference starts.
 *
 * Like a calendar date it is NOT converted: 10:00 in the organization's zone is 10:00 on every
 * reader's screen. The hour and minute are read off the string and formatted as a local time
 * with those parts — 12-hour, `2-digit` hours, never seconds (the form does not collect them).
 */
export function formatClockTime(value: string | null | undefined): string {
  const match = value ? /^(\d{2}):(\d{2})/.exec(value) : null;

  if (!match) {
    return EMPTY_DATE;
  }

  return CLOCK_TIME_FORMAT.format(new Date(2000, 0, 1, Number(match[1]), Number(match[2])));
}

/**
 * A `YYYY-MM-DD` calendar date as a **local** `Date`, with no zone conversion — the
 * conversion-free path `formatDateOnly` reads and the one the calendar grid is built on.
 * Returns null for anything that is not a calendar date, so a caller can fall back rather
 * than render an invented day.
 *
 * Noon, not midnight. In a zone whose DST transition lands at midnight that hour does not
 * exist, and the date the browser settles on is the day either side of the one asked for —
 * a calendar cell labelled 29 that selects the 28th. Noon is twelve hours from either edge,
 * so no transition can reach it.
 */
export function parseCalendarDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Whole years between a calendar date and today, in the viewer's own zone.
 *
 * **Counted in calendar parts, never by subtracting two instants.** `(now - born) / 31_557_600_000`
 * is the version everyone writes first and it is wrong twice: it averages leap years, so it
 * drifts by a day either side of a birthday, and it puts a calendar date through the instant
 * path that "a calendar date is not an instant" forbids — which for a reader west of UTC turns
 * a birthday into the day before it. Comparing month and day directly has neither problem and
 * needs no constant.
 *
 * Returns null rather than a number the field should not show:
 *  - no value, or a half-typed one (`1995-03-`) — the native date input reports those while the
 *    reader is still in the middle of the field, and an age flickering through 1,995 years as
 *    they type is worse than no age at all;
 *  - a date in the future, which has no age. The caller's `max` should already refuse it, but
 *    the readout must not print a negative number in the moment before the form does.
 */
export function calendarAge(value: string | null | undefined, asOf?: string): number | null {
  const born = parseCalendarDate(value);
  const today = parseCalendarDate(asOf) ?? new Date();

  if (!born) {
    return null;
  }

  let age = today.getFullYear() - born.getFullYear();

  // Not yet had this year's birthday — the whole of the correction, and why no constant is
  // needed. A 29 February birth turns on 1 March in a common year by this comparison.
  const monthsIn = today.getMonth() - born.getMonth();
  if (monthsIn < 0 || (monthsIn === 0 && today.getDate() < born.getDate())) {
    age -= 1;
  }

  return age < 0 ? null : age;
}

const CALENDAR_WEEKDAY_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'long' });
const CALENDAR_MONTH_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });

/**
 * The calendar footer’s label — the weekday, then the product’s standard date:
 * `Thursday, 09 May 2019` where the locale puts the day first, `Thursday, May 09, 2019` where
 * it does not. The ORDER is the viewer’s and the PARTS are ours — the same bargain every other
 * date in this file strikes.
 *
 * It replaces `formatCalendarDay` ("Friday 15th"), which named the weekday and the day and
 * left the reader to take the month and year off the pickers above. In a panel whose month can
 * now be changed without changing the selection, that was the one thing the header stopped
 * being able to tell them.
 *
 * A calendar date, so it takes the conversion-free path: the circle is drawn on a cell the grid
 * computed from the same parts, and putting the label through the instant path would name a
 * different day from the one marked.
 *
 * Composed from the two formatters this file already owns rather than a third `Intl` instance —
 * which is also why it is here and not at the call site.
 */
export function formatCalendarFull(value: string | null | undefined): string {
  const date = parseCalendarDate(value);

  if (!date) {
    return EMPTY_DATE;
  }

  return `${CALENDAR_WEEKDAY_FORMAT.format(date)}, ${DATE_FORMAT.format(date)}`;
}

/** The calendar header's trailing label — `January 2016`. Takes a `YYYY-MM` month. */
export function formatCalendarMonth(month: string): string {
  const date = parseCalendarDate(`${month}-01`);
  return date ? CALENDAR_MONTH_FORMAT.format(date) : EMPTY_DATE;
}

const MONTH_NAME_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'long' });

/**
 * A month's name alone — `October`. Takes a `YYYY-MM` month. For a heading whose year is
 * stated once above it, such as a column of Building Management's year calendar.
 */
export function formatMonthName(month: string): string {
  const date = parseCalendarDate(`${month}-01`);
  return date ? MONTH_NAME_FORMAT.format(date) : EMPTY_DATE;
}

const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

/**
 * A weekday's short name, uppercased — `MON`. The calendar grid's column headings.
 *
 * It lives here rather than beside the grid for the reason every other formatter does: this
 * file is the one place a date becomes a string, and an `Intl` formatter in a component
 * folder is the first of the two copies that eventually disagree. The grid still owns which
 * day the week *starts* on; this only names one.
 */
export function formatWeekdayShort(date: Date): string {
  return WEEKDAY_SHORT_FORMAT.format(date).toUpperCase();
}


/**
 * Date and time in the viewer's zone, 12-hour with AM/PM.
 *
 * Seconds render only when the instant carries them. A value recorded to the minute has no
 * seconds to show, and printing `:00` on it would claim a precision the record does not
 * have; an audit row written at 17:33:47 shows all three, because there the second is the
 * whole point of the timestamp.
 */
export function formatDateTime(value: string | null | undefined): string {
  const date = parseUtc(value);

  if (!date) {
    return EMPTY_DATE;
  }

  return (date.getSeconds() === 0 ? DATE_TIME_FORMAT : DATE_TIME_SECONDS_FORMAT).format(date);
}

/**
 * An instant as a `datetime-local` input's value — `YYYY-MM-DDTHH:mm` in the viewer's zone. The
 * one way an instant goes INTO a date-time box; `new Date(local).toISOString()` is the way back.
 * Through `parseUtc`, so a server timestamp carrying no `Z` is read as UTC rather than as local
 * time — reading it directly shifts it by the viewer's offset.
 */
export function toDateTimeLocal(value: string | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseUtc(value);

  if (!date) {
    return '';
  }

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** For a <time> element's dateTime attribute — always the machine-readable ISO form. */
export function toIso(value: string | null | undefined): string | undefined {
  return parseUtc(value)?.toISOString();
}

/**
 * Coarse relative phrasing for recency ("2 hours ago"). Falls back to an absolute date past
 * a week, where relative wording stops being easier to read than the date itself.
 */
export function formatRelative(value: string | null | undefined): string {
  const date = parseUtc(value);

  if (!date) {
    return EMPTY_DATE;
  }

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} d ago`;

  return formatDate(value);
}

/*
  THE LIFECYCLE TIMELINE'S TWO FORMATTERS, and its grouping key.

  Added 29 Sep 2026 with `<LifecycleTimeline>`, which splits one instant across two slots — a
  DAY BAND over a run of events and a TIME beside each one — so neither `formatDate` nor
  `formatDateTime` is the right shape for either half. They live here for the reason every
  other formatter does: a call site that formats its own date is one that will format the next
  one differently.
*/

/**
 * The time alone, 12-hour with AM/PM — `1:01 PM`.
 *
 * NUMERIC hours rather than the `2-digit` every other formatter here takes, and the departure
 * is deliberate: those render in mono tabular-nums COLUMNS, where a 1 against an 11 would
 * shift every row beside it. A timeline's time is right-aligned in a fixed gutter, so the
 * digits already line up and a leading zero is a character the reader has to skip.
 */
const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** The day band over a run of events — `SEP 22, 2026`, uppercased by the component. */
const DAY_BAND_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
});

export function formatTimeOfDay(value: string | null | undefined): string {
  const date = parseUtc(value);

  return date ? TIME_FORMAT.format(date) : EMPTY_DATE;
}

export function formatDayBand(value: string | null | undefined): string {
  const date = parseUtc(value);

  return date ? DAY_BAND_FORMAT.format(date) : EMPTY_DATE;
}

/**
 * The key a timeline groups its events by — one band per calendar day IN THE VIEWER'S ZONE.
 *
 * Built from the LOCAL parts, never from the ISO string's own date. An event at 22:00 Manila
 * is the 14th there and the 13th in UTC, so slicing the ISO form would file it under a band
 * whose own label — formatted in the viewer's zone — says the other day. Two surfaces reading
 * one instant and disagreeing about which day it fell on is the whole failure `formatDateOnly`
 * exists to prevent, arriving one level up.
 */
export function dayKeyOf(value: string | null | undefined): string | null {
  const date = parseUtc(value);

  if (!date) {
    return null;
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
}
